// Page sizes, API fetch limits and how many items a view shows.

/** Rows per page on paged lists (Surat Jalan, tabung, pengguna, lokasi, gateway, versi). */
export const PAGE_SIZE = 25;
/** = the backend's PAGE.max (offset lists) and CURSOR_PAGE.max (keyset lists): the most one request may ask for. */
export const API_PAGE_MAX = 100;
export const API_CURSOR_MAX = 200;
/** Keyset lists (peringatan, tabung, Surat Jalan): rows per page the user can choose. */
export const LIST_PAGE_SIZES = [25, 50, 100] as const;
export const ALERT_PAGE_DEFAULT = 50;
/** = the backend's search minimum: a shorter search is not sent (said under the box instead). */
export const CYLINDER_SEARCH_MIN = 3;
/** The retest window ("soon") until GET /cylinders/summary says the configured one (= the backend's default). */
export const TEST_DUE_DAYS_DEFAULT = 30;
/** = the backend's CYLINDERS.reason: the reason given for a cylinder's status change, characters (trimmed). */
export const STATUS_REASON = { min: 5, max: 500 } as const;
/** = the backend's TEXT.note: a note on taking or closing an alert, characters. */
export const ALERT_NOTE_MAX = 1_000;
/** Peringatan in the header's bell: the worst few that need action. */
export const BELL_ALERTS = 5;
/** The bell's badge shows "99+" past this. */
export const BADGE_MAX = 99;
/** Lists inside a drawer or a detail card (a user's devices and activity, a location's children): rows per page. */
export const DRAWER_PAGE_SIZE = 10;
/** The latest activity previewed in the user drawer (= the backend's recentActivity length). */
export const RECENT_ACTIVITY = 5;
/**
 * A calm hint on a network location's stock ("Stok sedikit") at or under this many cylinders; 0 is "Kosong". Only a
 * hint until the per-type thresholds of phase 2 replace it.
 */
export const LOW_STOCK_HINT = 5;
/** Lokasi › Jaringan: locations fetched per "Muat lebih banyak", at the top of the tree and under one location. */
export const TREE_PAGE_SIZE = { top: 25, children: 20 } as const;
/** Lokasi › Jaringan: deeper rows are indented as this level (= the .lvl-N rules in locations.css). */
export const TREE_MAX_INDENT = 5;
/** Search pickers (lokasi tugas, tempat gateway): results shown per search. */
export const PICKER_LIMIT = 20;
/**
 * Districts and villages (GET /regions/districts, /regions/villages): one page of a parent's list (a regency has at
 * most a few dozen districts, a district rarely more than this many villages; past it the picker asks to type), and
 * the shortest name the server searches (= the backend's REGION_LIST.searchMin).
 */
export const REGION_LIST = { pageSize: 100, searchMin: 3 } as const;
/** Bengkel Uji: the next retest at most this many years after the test day (= the backend's CYLINDER_TEST). */
export const CYLINDER_TEST = { maxIntervalYears: 10, reason: { min: 3, max: 500 } } as const;
/** Kirim Tabung: likely destinations offered as cards (children of this location first, then its siblings). */
export const DISPATCH_SUGGESTIONS = 6;

/** Log aktivitas: rows per page the user can choose (the backend allows up to 200). */
export const AUDIT_PAGE_SIZES = [25, 50, 100] as const;
export const AUDIT_PAGE_DEFAULT = 50;
/** = the backend's AUDIT_EXPORT_MAX: one CSV download holds at most this many (newest) rows. */
export const AUDIT_EXPORT_MAX = 50_000;
/** Log aktivitas date presets: the last N WIB days including today. */
export const AUDIT_DATE_PRESETS = [1, 7, 30] as const;

/** `limit=` sent to list endpoints that are not paged. */
export const FETCH_LIMIT = {
  loadingAtLocation: 20,
  locationDay: 50,
  locationShipments: 8,
  /** Terima Kiriman: the latest finished notes to this place, of which today's are shown */
  receivedRecent: 10,
  gatewayCommands: 15,
  /** the readers placed at the operator's location (one or two in practice) */
  locationGateways: 20,
  /** Catat hasil uji: the cylinders at the workshop listed to pick from (the rest by serial number search) */
  testStock: 20,
  /** Operasional home: the latest events asked per reader, before repeated reads of one tag are grouped */
  operationsReads: 40,
  /** Operasional home: the readers of the location whose reads are shown (one or two in practice) */
  operationsReaders: 2,
  /** tags in front of a reader (lookup, registration) */
  presentTags: 100,
  /** Pembaruan: online gateways offered as the test gateway of a rollout */
  canaryCandidates: 20,
  serialSearch: 10,
  /** replay: Surat Jalan per page (the backend allows 200) and cylinder arrivals per page (it allows 200) */
  replayShipments: 200,
  replayMovements: 200,
} as const;

/** Replay: at most this many pages are read; beyond that the replay says it shows part of the day. */
export const REPLAY_MAX_PAGES = { shipments: 5, movements: 25 } as const;

/** How many items a view renders before summarising the rest. */
export const SHOWN = {
  /** Ruang Kendali's side panel: rows per tab (Aktivitas, Surat Jalan, Tindakan) before "Lihat semua" */
  overviewPanelRows: 8,
  /** Operasional home: today's Surat Jalan per direction (Keluar, Masuk) before "Lihat semua" */
  operationsHomeRows: 5,
  /** Operasional home: "Pembacaan terakhir", one row per tag (repeated reads grouped) */
  operationsHomeReads: 5,
  needsActionSeen: 5,
  feedInitialAlerts: 10,
  feedLiveItems: 20,
  mapPanelRecent: 4,
  toasts: 4,
  /** critical alerts already toasted, remembered so one alert never toasts twice (oldest forgotten first) */
  criticalToastMemory: 200,
  rejectCards: 3,
  receivedPreview: 5,
  /** Terima Kiriman: notes received here today, under the incoming list */
  receivedToday: 5,
  notYetReceived: 8,
  documentPreview: 10,
  presentTags: 12,
  registeredRecent: 5,
  missingSerials: 3,
  /** a 24 h screen remembers at most this many Surat Jalan from live messages (the oldest are forgotten first) */
  liveShipmentMemory: 200,
  /** replay panel: the newest steps up to the time shown */
  replaySteps: 3,
  /** a location page's cards (cylinders, movements, children, readers, Surat Jalan): rows before "Lihat semua" */
  locationPreview: 5,
  /** a cylinder page's cards (journey, reads, Surat Jalan, open alerts): rows before "Lihat semua" */
  cylinderPreview: 5,
  /** a Surat Jalan page's cards (cargo, discrepancy, related alerts, rejections): rows before "Lihat semua" */
  shipmentPreview: 5,
  /** a reader page's cards (alerts, reads, commands, data recovery): rows before "Lihat semua" */
  readerPreview: 5,
  /** a reader page's update card: the newest steps of its update history */
  readerUpdates: 3,
} as const;

/** Pembaruan: the shares a staged rollout offers (percent of the target's gateways). */
export const ROLLOUT_PERCENTS = [5, 10, 25, 50] as const;

export const PASSWORD_MIN_LENGTH = 10;
/** = the backend's passwordProblem() */
export const PASSWORD_MAX_LENGTH = 200;
/** A person's display name (= the backend's TEXT.name). */
export const NAME_MAX_LENGTH = 120;
export const RECEIVE_NOTE_MAX_LENGTH = 1_000;
/** Antenna power accepted by the reader (dBm), and the value shown when the reader never reported one. */
export const ANTENNA_POWER = { min: 0, max: 30, fallback: 20 } as const;
/** Production / retest dates outside these years are typos. */
export const DATE_YEAR_RANGE = { min: 1950, max: 2100 } as const;
/** Registrasi: the years offered for the production date (back from this year) and the next retest (ahead). */
export const REGISTER_YEARS = { madeBack: 15, dueAhead: 15 } as const;
/** Days of history behind the overview sparklines and trend chart (the backend allows at most 60). */
export const TREND_DAYS = 14;

/** Location photos: the backend keeps at most `maxPerLocation`; the browser scales a large picture down before sending it. */
export const PHOTO = {
  maxPerLocation: 12,
  /** longest edge sent (px): a phone camera's 12 MP picture becomes about 2000 x 1500 */
  maxEdge: 2000,
  /** a picture already within maxEdge is sent as it is, unless its file is larger than this (bytes) */
  keepBelowBytes: 2_500_000,
  /** WebP (or JPEG where the browser cannot write WebP) quality of a scaled picture */
  quality: 0.85,
  /** a transparent picture is flattened on this colour (JPEG has no transparency; photos are shown on white) */
  flattenOn: '#ffffff',
  /** uploads running at the same time: one, so the gallery keeps the order the files were picked in */
  concurrency: 1,
} as const;

/** Akun: rows a card previews (devices, sign-ins) before "Lihat semua (N)" opens the whole list in a drawer. */
export const ACCOUNT_PREVIEW_ROWS = 5;
/** Two-step verification: digits of an authenticator code (RFC 6238, = the backend's TOTP.digits). */
export const TOTP_CODE_LENGTH = 6;
/** A recovery code as typed (letters, digits, dashes, spaces); longer input is not a recovery code. */
export const RECOVERY_CODE_MAX_LENGTH = 32;

/**
 * Hub registration: lengths and formats the form checks before sending (the backend checks them again), and the
 * fallbacks used until the requirements answer (which carries the real upload limits and GPS accuracy).
 */
export const HUB_FORM = {
  nameMin: 3,
  nameMax: 120,
  personMax: 80,
  labelMax: 80,
  shortMax: 60,
  addressMin: 5,
  addressMax: 300,
  noteMax: 1_000,
  /** a staff note or reason the applicant reads: at least this long */
  reasonMin: 5,
  /** NIK: 16 digits */
  nikLength: 16,
  /** NPWP: 15 (old) or 16 digits */
  npwpLengths: [15, 16],
  /** NIB: 13 digits */
  nibLength: 13,
  /** KBLI: 5 digits */
  kbliLength: 5,
  phoneDigits: { min: 9, max: 15 },
  accountDigits: { min: 6, max: 20 },
  areaM2: { min: 4, max: 100_000 },
  storageCapacity: { min: 10, max: 100_000 },
  distanceM: { min: 0, max: 100_000 },
  /** a lease or loan must still run this long (years ahead offered in the date field) */
  validUntilYearsAhead: 30,
  /** the backend's caps (HUB_REGISTRATION): an image after it is scaled down, a PDF */
  maxImageBytes: 10 * 1024 * 1024,
  maxPdfBytes: 5 * 1024 * 1024,
  /** a picture picked may be this large: it is scaled down in the browser before it is sent */
  maxPickBytes: 40 * 1024 * 1024,
  uploadTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
  /** uploads running at once (a phone on a slow line: two) */
  uploadConcurrency: 2,
  /** automatic retries of an upload that failed for a passing reason (network, 5xx, 429), then a manual "Coba lagi" */
  uploadAutoRetries: 3,
  /** a photo is scaled down to this edge before it is sent (the server re-encodes it anyway) */
  /** the backend re-encodes inside a 2000 px square anyway: sending more is wasted on a slow line */
  photo: { maxEdge: 2000, keepBelowBytes: 1_500_000, quality: 0.85, flattenOn: '#ffffff' },
  trackingCode: /^HUB-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/,
} as const;

/** The verifier's visit: the device position must be at least this precise (metres). */
export const VISIT_GPS_MAX_ACCURACY_M = 100;
/** Staff list of applications: one page. */
export const HUB_APPLICATION_PAGE_SIZE = 25;

/** Households (Phase 3): lists, the KK photo, reasons (= the backend's HOUSEHOLDS limits). */
export const HOUSEHOLD_FORM = {
  reasonMin: 5,
  reasonMax: 500,
  noteMax: 500,
  photoTypes: ['image/jpeg', 'image/png', 'image/webp'],
  maxPhotoBytes: 10 * 1024 * 1024,
  maxPickBytes: 40 * 1024 * 1024,
  /** a KK photo stays readable at 2000 px (small print); the server re-encodes it anyway */
  photo: { maxEdge: 2000, keepBelowBytes: 1_500_000, quality: 0.85, background: '#ffffff' },
  /** the import: the largest CSV sent and a batch's label */
  importMaxBytes: 5 * 1024 * 1024,
  sourceLabelMax: 80,
} as const;
export const HOUSEHOLD_PAGE_SIZE = 25;

/** Orders (Phase 3): the exchange's photo, reasons, the confirmation code (= the backend's ORDERS limits). */
export const ORDER_FORM = {
  reasonMin: 5,
  reasonMax: 300,
  confirmationLength: 6,
  /** the largest cash amount typed (rupiah): a typo guard, the server has its own bound */
  cashMax: 10_000_000,
  photoTypes: ['image/jpeg', 'image/png', 'image/webp'],
  maxPhotoBytes: 10 * 1024 * 1024,
  maxPickBytes: 40 * 1024 * 1024,
  photo: { maxEdge: 1600, keepBelowBytes: 1_000_000, quality: 0.8, background: '#ffffff' },
  /** the device position at the house: coarser than this is sent, with a warning */
  gpsWarnAccuracyM: 100,
} as const;
export const ORDER_PAGE_SIZE = 25;
/** An open delivery due within this many hours is "segera" (amber before it is late). */
export const ORDER_DUE_SOON_HOURS = 4;
/** Cylinder labels per printed request, and the A4 sheet's grid (3 × 8 = 24 per page). */
export const LABEL_SHEET = { cols: 3, rows: 8 } as const;
/**
 * How a cylinder is identified on a phone: its serial number (QR label or typed, capitals, digits, dashes, dots) or a
 * tag's EPC (a phone Reader over OTG types it in hex: 64 to 128 bits, 96 typically).
 */
export const CYLINDER_ID = { serial: /^[A-Z0-9][A-Z0-9._/-]*$/, serialMax: 40, epc: /^(?:[0-9A-F]{4}){4,8}$/ } as const;
/** "Cari tabung": at most this many serials looked for at once, and asked of the lookup per request. */
export const SWEEP = { maxTargets: 500, lookupBatch: 100, sightingBatch: 50 } as const;

/** Statistik: the longest custom period the API takes (days), and the region table's page. */
export const STATISTICS = { maxRangeDays: 366, regionPage: 20, regionPagePhone: 10, phoneMedia: '(max-width: 640px)', /** while the figures are being recomputed, asked again this often (ms) */ rebuildingRefreshMs: 30_000 } as const;
